import Container from '../components/Container';
import Button from '../components/Button';
import Eyebrow from '../components/Eyebrow';
import PrimitiveCard from '../components/PrimitiveCard';
import HeroCodeCycler from '../components/HeroCodeCycler';
import LiveEventTicker from '../components/LiveEventTicker';
import PrismDiagram from '../components/PrismDiagram';
import CountUp from '../components/CountUp';
import { Shield, HardDrive, MessageSquare, Zap, Check, ArrowRight } from 'lucide-react';

export default function Landing() {
  return (
    <main>
      {/* HERO ====================================================== */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 -top-32 mx-auto h-[520px] max-w-[1100px] opacity-[0.65] blur-3xl"
          style={{
            background:
              'radial-gradient(60% 50% at 50% 50%, rgba(124,58,237,0.16) 0%, rgba(236,72,153,0.08) 45%, transparent 70%)',
          }}
        />
        <Container className="relative pt-20 pb-20">
          <div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <span className="inline-flex items-center gap-2 rounded-full border border-border bg-surface px-3 py-1 text-[12px] text-body shadow-hair">
                <span className="relative grid place-items-center">
                  <span className="absolute size-3 animate-pulse-soft rounded-full bg-ok/30" />
                  <span className="size-1.5 rounded-full bg-ok" />
                </span>
                Auth shipping today · v1.0 · production-ready
              </span>

              <h1 className="mt-6 font-bold tracking-tighter text-ink text-[64px] leading-[1.02] sm:text-[80px] lg:text-[88px] animate-fade-in">
                Primitives <br />
                that just <span className="bg-pryma-grad bg-clip-text text-transparent">ship.</span>
              </h1>

              <div className="mt-7 flex h-[3px] w-[480px] max-w-full overflow-hidden rounded-full">
                <span className="flex-1 bg-violet" />
                <span className="flex-1 bg-violetMid" />
                <span className="flex-1 bg-cyan" />
                <span className="flex-1 bg-amber" />
                <span className="flex-1 bg-pink" />
              </div>

              <p className="mt-7 max-w-[560px] text-[19px] leading-[1.55] text-body">
                Auth, database, comms, and realtime — bundled into one SDK. The base layer for
                everything you build next.
              </p>

              <div className="mt-9 flex flex-wrap items-center gap-3">
                <Button variant="primary" size="lg">
                  Start building free <ArrowRight className="size-4" />
                </Button>
                <Button variant="secondary" size="lg">
                  Read the docs
                </Button>
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-4 font-mono text-[12px] text-muted">
                <code className="rounded-md border border-border bg-surface px-2.5 py-1 text-ink/80">
                  $ npm install pryma
                </code>
                <span>·</span>
                <span>14.2 KB gzipped</span>
                <span>·</span>
                <span>ESM · CJS · TypeScript</span>
              </div>
            </div>

            <div className="lg:col-span-5">
              <HeroCodeCycler />
            </div>
          </div>
        </Container>
      </section>

      {/* LIVE EVENT TICKER (replaces static "trusted by") =========== */}
      <section className="border-y border-border bg-slate50">
        <Container className="py-12">
          <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <Eyebrow color="violet">Pryma in production · right now</Eyebrow>
              <h2 className="mt-3 text-[28px] font-bold leading-[1.15] tracking-tighter text-ink">
                <CountUp to={2_417_882} /> sign-ins yesterday.
              </h2>
              <p className="mt-3 text-[15px] text-body">
                Live audit stream — the same feed you'd see in the Pryma console. Fewer
                abstractions, more truth.
              </p>
              <p className="mt-4 inline-flex items-center gap-1.5 font-mono text-[12px] text-violet">
                status.pryma.dev <ArrowRight className="size-3.5" />
              </p>
            </div>
            <div className="lg:col-span-8">
              <LiveEventTicker />
            </div>
          </div>
        </Container>
      </section>

      {/* PRISM SHOWPIECE =========================================== */}
      <section className="relative overflow-hidden">
        <Container className="py-24">
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <Eyebrow color="pink">The metaphor, in motion</Eyebrow>
              <h2 className="mt-4 text-[44px] font-bold leading-[1.05] tracking-tighter text-ink">
                One light. <br />
                <span className="bg-pryma-grad bg-clip-text text-transparent">
                  Four primitives.
                </span>
              </h2>
              <p className="mt-5 text-[16px] leading-[1.55] text-body">
                Your app sends a single signal. Pryma refracts it into auth, data, comms, and
                realtime — the four base-layer primitives every product needs.
              </p>
              <ul className="mt-7 space-y-3 text-[14px]">
                {[
                  ['One SDK', 'pryma.auth · pryma.db · pryma.comms · pryma.realtime'],
                  ['One identity', 'sign-in carries through to every primitive'],
                  ['One bill', 'no surprise vendor sprawl across 4 line items'],
                ].map(([t, d]) => (
                  <li key={t} className="flex gap-3">
                    <span className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full border border-violet bg-violetSoft">
                      <Check className="size-3 text-violet" />
                    </span>
                    <div>
                      <span className="font-bold text-ink">{t}</span>
                      <span className="ml-2 font-mono text-[12px] text-muted">{d}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-8">
              <PrismDiagram />
            </div>
          </div>
        </Container>
      </section>

      {/* PRIMITIVES GRID =========================================== */}
      <section className="border-t border-border bg-slate50">
        <Container className="py-24">
          <div className="grid grid-cols-1 items-end gap-6 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <Eyebrow color="violet">Four primitives</Eyebrow>
              <h2 className="mt-3 text-[44px] font-bold leading-[1.1] tracking-tighter text-ink">
                One foundation. <span className="text-muted">Four powers.</span>
              </h2>
            </div>
            <p className="text-[16px] text-body lg:col-span-5">
              Built to compose. Shipped one at a time. <br />
              Use what you need today, layer in more tomorrow.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <PrimitiveCard
              accent="violet"
              status="live"
              title="Auth"
              tagline="OAUTH · MAGIC LINKS · MFA"
              body="Drop-in authentication with OAuth, magic links, sessions, and full MFA. Multi-tenant from the first call."
              link="Read auth docs"
              icon={<Shield className="size-5 text-violet" />}
            />
            <PrimitiveCard
              accent="cyan"
              status="beta"
              title="Store"
              tagline="SQLITE · KV · FILES"
              body="Managed SQLite, a fast KV store, and file storage — all tenant-aware and wired into auth."
              link="Preview the schema"
              icon={<HardDrive className="size-5 text-cyan" />}
            />
            <PrimitiveCard
              accent="amber"
              status="soon"
              title="Comms"
              tagline="TRANSACTIONAL · WEBHOOKS · EMAIL"
              body="Send mail, fire webhooks, and receive replies — wired straight to the users you already have."
              link="Get on the waitlist"
              icon={<MessageSquare className="size-5 text-amber" />}
            />
            <PrimitiveCard
              accent="pink"
              status="soon"
              title="Realtime"
              tagline="PRESENCE · CHANNELS · BROADCAST"
              body="Streams, presence, and pub/sub with auth and policy already baked into every channel."
              link="Get on the waitlist"
              icon={<Zap className="size-5 text-pink" />}
            />
          </div>
        </Container>
      </section>

      {/* STATS WITH ANIMATED COUNTERS ============================== */}
      <section className="border-t border-border">
        <Container className="py-20">
          <p className="text-center text-[11px] font-bold uppercase tracking-widest text-muted">
            By the numbers
          </p>
          <div className="mt-10 grid grid-cols-1 gap-12 sm:grid-cols-3">
            {[
              {
                num: 1,
                color: 'text-violet',
                label: 'ONE SDK',
                sub: 'TypeScript-first SDK with every primitive on board.',
              },
              {
                num: 4,
                color: 'text-cyan',
                label: 'FOUR PRIMITIVES',
                sub: 'Auth, DB, comms, realtime — same project, same auth.',
              },
              {
                num: 0,
                color: 'text-pinkMid',
                label: 'ZERO GLUE CODE',
                sub: 'No CORS gymnastics, no JWT plumbing, no manual polling.',
              },
            ].map(s => (
              <div key={s.label} className="text-center">
                <p className={`text-[112px] font-bold leading-none tracking-tighter ${s.color}`}>
                  <CountUp to={s.num} durationMs={s.num === 0 ? 0 : 900} />
                </p>
                <p className="mt-4 text-[12px] font-bold uppercase tracking-wider text-ink">
                  {s.label}
                </p>
                <p className="mx-auto mt-2 max-w-[320px] text-[14px] text-body">{s.sub}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* FINAL CTA ================================================= */}
      <section>
        <Container className="py-24">
          <div className="relative overflow-hidden rounded-3xl border border-border bg-surface px-8 py-16 shadow-hair">
            <div className="absolute inset-x-0 top-0 flex h-[4px]">
              <span className="flex-1 bg-violet" />
              <span className="flex-1 bg-cyan" />
              <span className="flex-1 bg-amber" />
              <span className="flex-1 bg-pink" />
            </div>
            <div
              aria-hidden
              className="pointer-events-none absolute -bottom-32 left-1/2 h-[320px] w-[700px] -translate-x-1/2 rounded-full opacity-60 blur-3xl"
              style={{
                background:
                  'radial-gradient(closest-side, rgba(124,58,237,0.18), rgba(236,72,153,0.10), transparent)',
              }}
            />
            <div className="relative mx-auto max-w-[860px] text-center">
              <Eyebrow color="violet">Ready?</Eyebrow>
              <h2 className="mt-5 text-[56px] font-bold leading-[1.05] tracking-tighter text-ink sm:text-[64px]">
                Stop wiring. <br />
                <span className="bg-pryma-grad bg-clip-text text-transparent">Start shipping.</span>
              </h2>
              <p className="mx-auto mt-6 max-w-[600px] text-[17px] leading-[1.55] text-body">
                Pryma takes care of the boring bits so you can launch the thing.
              </p>
              <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
                <Button variant="primary" size="lg">
                  Start free <ArrowRight className="size-4" />
                </Button>
                <Button variant="secondary" size="lg">
                  Talk to founders
                </Button>
              </div>
              <ul className="mt-9 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[13px] text-body">
                {['Free for 50K MAU', 'No credit card', 'SOC 2 Type II', '99.99% uptime'].map(t => (
                  <li key={t} className="inline-flex items-center gap-1.5">
                    <Check className="size-3.5 text-ok" /> {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Container>
      </section>
    </main>
  );
}
